import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Clock, Plus, Trash2, Edit, Calendar, Users, 
  FileText, PlayCircle, Settings2
} from 'lucide-react';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const TEMPLATE_COLORS = [
  { value: '#10B981', label: 'Green' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#EF4444', label: 'Red' },
  { value: '#D4A853', label: 'Gold' },
];

const ShiftTemplatesManager = ({ getAuthHeaders, staffList, classrooms, onTemplateApplied }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    start_time: '08:00',
    end_time: '16:00',
    default_classroom: '',
    color: '#10B981'
  });
  
  const [applyForm, setApplyForm] = useState({
    staff_ids: [],
    dates: [],
    classroom_override: ''
  });

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/schedule/templates`, getAuthHeaders());
      setTemplates(res.data);
    } catch (error) {
      console.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSeedDefaults = async () => {
    try {
      const res = await axios.post(`${API}/schedule/templates/seed-defaults`, {}, getAuthHeaders());
      toast.success(res.data.message);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to create default templates');
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    
    if (!templateForm.name || !templateForm.start_time || !templateForm.end_time) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (editingTemplate) {
        await axios.put(`${API}/schedule/templates/${editingTemplate.id}`, templateForm, getAuthHeaders());
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/schedule/templates`, templateForm, getAuthHeaders());
        toast.success('Template created');
      }
      setCreateDialogOpen(false);
      setEditingTemplate(null);
      setTemplateForm({
        name: '',
        description: '',
        start_time: '08:00',
        end_time: '16:00',
        default_classroom: '',
        color: '#10B981'
      });
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    
    try {
      await axios.delete(`${API}/schedule/templates/${templateId}`, getAuthHeaders());
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      start_time: template.start_time,
      end_time: template.end_time,
      default_classroom: template.default_classroom || '',
      color: template.color || '#10B981'
    });
    setCreateDialogOpen(true);
  };

  const openApplyTemplate = (template) => {
    setSelectedTemplate(template);
    setApplyForm({
      staff_ids: [],
      dates: [],
      classroom_override: template.default_classroom || ''
    });
    setApplyDialogOpen(true);
  };

  const handleApplyTemplate = async (e) => {
    e.preventDefault();
    
    if (applyForm.staff_ids.length === 0 || applyForm.dates.length === 0) {
      toast.error('Please select at least one staff member and one date');
      return;
    }

    try {
      const payload = {
        template_id: selectedTemplate.id,
        staff_ids: applyForm.staff_ids,
        dates: applyForm.dates,
        classroom_override: applyForm.classroom_override || null
      };
      
      const res = await axios.post(`${API}/schedule/templates/apply`, payload, getAuthHeaders());
      
      if (res.data.shifts_created > 0) {
        toast.success(`Created ${res.data.shifts_created} shifts`);
      }
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} shifts skipped (already exist)`);
      }
      
      setApplyDialogOpen(false);
      
      // Pass the first date to callback so calendar can navigate to that week
      const firstDate = applyForm.dates.length > 0 ? applyForm.dates.sort()[0] : null;
      onTemplateApplied?.(firstDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply template');
    }
  };

  const toggleStaffSelection = (staffId) => {
    setApplyForm(prev => ({
      ...prev,
      staff_ids: prev.staff_ids.includes(staffId)
        ? prev.staff_ids.filter(id => id !== staffId)
        : [...prev.staff_ids, staffId]
    }));
  };

  const toggleDateSelection = (date) => {
    setApplyForm(prev => ({
      ...prev,
      dates: prev.dates.includes(date)
        ? prev.dates.filter(d => d !== date)
        : [...prev.dates, date]
    }));
  };

  // Generate dates for next 2 weeks
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const getDuration = (start, end) => {
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const mins = (eh * 60 + em) - (sh * 60 + sm);
    const hours = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return remainMins > 0 ? `${hours}h ${remainMins}m` : `${hours}h`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="shift-templates-manager">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Shift Templates
            </CardTitle>
            <CardDescription>Quick-apply pre-defined shifts to staff schedules</CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            {templates.length === 0 && (
              <Button variant="outline" size="sm" onClick={handleSeedDefaults}>
                <Settings2 className="w-4 h-4 mr-1" />
                Create Defaults
              </Button>
            )}
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" onClick={() => {
                  setEditingTemplate(null);
                  setTemplateForm({
                    name: '',
                    description: '',
                    start_time: '08:00',
                    end_time: '16:00',
                    default_classroom: '',
                    color: '#10B981'
                  });
                }}>
                  <Plus className="w-4 h-4 mr-1" />
                  New Template
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Shift Template'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSaveTemplate} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Template Name *</Label>
                    <Input
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                      placeholder="e.g., Full Day (8am-4pm)"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={templateForm.description}
                      onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
                      placeholder="Optional description"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Time *</Label>
                      <Input
                        type="time"
                        value={templateForm.start_time}
                        onChange={(e) => setTemplateForm({...templateForm, start_time: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Time *</Label>
                      <Input
                        type="time"
                        value={templateForm.end_time}
                        onChange={(e) => setTemplateForm({...templateForm, end_time: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Default Classroom</Label>
                    <Select 
                      value={templateForm.default_classroom} 
                      onValueChange={(v) => setTemplateForm({...templateForm, default_classroom: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Any / Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Any / Unassigned</SelectItem>
                        {classrooms?.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Color</Label>
                    <div className="flex gap-2 flex-wrap">
                      {TEMPLATE_COLORS.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            templateForm.color === color.value ? 'border-gray-800 scale-110' : 'border-transparent'
                          }`}
                          style={{ backgroundColor: color.value }}
                          onClick={() => setTemplateForm({...templateForm, color: color.value})}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      {editingTemplate ? 'Update' : 'Create'} Template
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {templates.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No shift templates yet</p>
            <p className="text-sm">Create templates to quickly schedule staff</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {templates.map((template) => (
              <div
                key={template.id}
                className="p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                style={{ borderLeftColor: template.color, borderLeftWidth: '4px' }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium">{template.name}</h4>
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" />
                      {formatTime(template.start_time)} - {formatTime(template.end_time)}
                      <span className="text-xs ml-1">({getDuration(template.start_time, template.end_time)})</span>
                    </p>
                    {template.default_classroom && (
                      <Badge variant="outline" className="mt-2 text-xs">
                        {template.default_classroom}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditTemplate(template)}>
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteTemplate(template.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <Button 
                  size="sm" 
                  className="w-full mt-3"
                  onClick={() => openApplyTemplate(template)}
                  data-testid={`apply-template-${template.id}`}
                >
                  <PlayCircle className="w-4 h-4 mr-1" />
                  Apply Template
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Apply Template Dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Apply Template: {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleApplyTemplate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Select Staff Members *
              </Label>
              <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                {staffList?.map((staff) => (
                  <div key={staff.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`staff-${staff.id}`}
                      checked={applyForm.staff_ids.includes(staff.id)}
                      onCheckedChange={() => toggleStaffSelection(staff.id)}
                    />
                    <label htmlFor={`staff-${staff.id}`} className="text-sm cursor-pointer">
                      {staff.user_name || 'Unknown'} - {staff.position}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {applyForm.staff_ids.length} staff selected
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Select Dates *
              </Label>
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto grid grid-cols-2 gap-2">
                {getAvailableDates().map((date) => {
                  const d = new Date(date + 'T12:00:00');
                  const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
                  const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  
                  return (
                    <div key={date} className="flex items-center gap-2">
                      <Checkbox
                        id={`date-${date}`}
                        checked={applyForm.dates.includes(date)}
                        onCheckedChange={() => toggleDateSelection(date)}
                      />
                      <label htmlFor={`date-${date}`} className="text-sm cursor-pointer">
                        {dayName}, {dateStr}
                      </label>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                {applyForm.dates.length} dates selected
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Classroom Override</Label>
              <Select 
                value={applyForm.classroom_override} 
                onValueChange={(v) => setApplyForm({...applyForm, classroom_override: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder={selectedTemplate?.default_classroom || 'Unassigned'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="use_default">Use template default</SelectItem>
                  {classrooms?.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-3 text-sm">
              <p className="font-medium">Summary</p>
              <p className="text-muted-foreground">
                This will create <strong>{applyForm.staff_ids.length * applyForm.dates.length}</strong> shifts
                ({applyForm.staff_ids.length} staff × {applyForm.dates.length} dates)
              </p>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setApplyDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={applyForm.staff_ids.length === 0 || applyForm.dates.length === 0}>
                Apply Template
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ShiftTemplatesManager;
